import { useEffect, useRef } from 'react'
import { useDispatch, useStore } from 'react-redux'

import { Switch } from '@thanx/uikit/switch'
import ThanxAIConversation from 'components/AI/ThanxAIConversation'
import { useAISegmentBuilderActions } from 'hooks/useAISegmentBuilderActions'
import useFlag from 'hooks/useFlag'
import { useAIChat } from 'hooks/websocket'
import { buildTranslate } from 'locales'
import { cleanupPickersBeforeSubmit } from 'scenes/Segments/AISegmentBuilder/ReviewItems/hooks'
import type { Message } from 'slices/aiConversations/types'
import {
  selectGroupDisabled,
  selectGroupIds,
  setGroupDisabled,
} from 'slices/aiSegmentBuilder/picker'

const t = buildTranslate('segments.ai_builder.conversation')

export interface AISegmentBuilderConversationProps {
  initialPrompt?: string
}

const AISegmentBuilderConversation = ({
  initialPrompt,
}: AISegmentBuilderConversationProps) => {
  const dispatch = useDispatch()
  const store = useStore()
  const {
    isConnected,
    connectionError,
    sendMessage,
    setIsLoading,
    setAwaitingResponse,
    addMessage,
    setMessages,
  } = useAIChat()

  // No explicit reconnect on create-another: the reset unmounts this component
  // and the next prompt remounts it, reconnecting with the now-null chat id.
  const { onCreateAnother } = useAISegmentBuilderActions()
  const aiSegmentDevtoolsEnabled = useFlag('ai-segment-devtools-enabled', false)

  // Track if the initial prompt has been sent to prevent duplicate sends
  const sentPromptRef = useRef<string | null>(null)

  useEffect(() => {
    // Only send if we have a prompt, are connected, and haven't sent this exact prompt yet
    if (
      initialPrompt &&
      isConnected &&
      sentPromptRef.current !== initialPrompt
    ) {
      sentPromptRef.current = initialPrompt

      const userMessage: Message = {
        id: `user-${Date.now()}`,
        content: initialPrompt,
        type: 'user',
        timestamp: new Date(),
        isMarkdown: false,
        sequenceNumber: 1,
        clientId: 'initial-prompt',
        isOptimistic: false,
      }
      setMessages([userMessage])
      sendMessage(initialPrompt)
    }
  }, [initialPrompt, isConnected, setMessages, sendMessage])

  const handleMessageSubmit = (messageContent: string) => {
    if (!isConnected || !messageContent.trim()) return

    // DATA-4323: Cleanup and disable all existing (unconfirmed) picker groups
    // before sending a new message. This collapses them to minimal form.
    const currentState = store.getState()
    const existingGroupIds = selectGroupIds(currentState)
    for (const groupId of existingGroupIds) {
      const isDisabled = selectGroupDisabled(currentState, groupId)
      if (!isDisabled) {
        // Clean up the picker group (remove empty pickers, update operators)
        cleanupPickersBeforeSubmit(dispatch, currentState, groupId)
        // Disable the group so it becomes read-only
        dispatch(setGroupDisabled({ groupId, disabled: true }))
      }
    }

    const newMessage: Message = {
      id: `user-${Date.now()}`,
      content: messageContent,
      type: 'user',
      timestamp: new Date(),
      isMarkdown: false,
      sequenceNumber: Date.now(),
      clientId: 'user-input',
      isOptimistic: false,
    }

    addMessage(newMessage)
    setIsLoading(true)
    setAwaitingResponse(true)
    sendMessage(messageContent)
  }

  return (
    <div>
      {/* Connection status indicator */}
      <Switch condition={aiSegmentDevtoolsEnabled && connectionError}>
        <div
          role="alert"
          className="alert alert-warning"
          style={{
            padding: '8px',
            background: '#fff3cd',
            color: '#856404',
            borderRadius: '4px',
            marginBottom: '8px',
          }}
        >
          <span aria-hidden="true">⚠️</span> {t('connection_error')}
        </div>
      </Switch>
      <Switch
        condition={aiSegmentDevtoolsEnabled && !isConnected && !connectionError}
      >
        <div
          role="status"
          aria-live="polite"
          className="alert alert-info"
          style={{
            padding: '8px',
            background: '#d4edda',
            color: '#155724',
            borderRadius: '4px',
            marginBottom: '8px',
          }}
        >
          <span aria-hidden="true">🔄</span> {t('connecting')}
        </div>
      </Switch>
      <Switch condition={aiSegmentDevtoolsEnabled && isConnected}>
        <div
          role="status"
          aria-live="polite"
          className="alert alert-success"
          style={{
            padding: '8px',
            background: '#d4edda',
            color: '#155724',
            borderRadius: '4px',
            marginBottom: '8px',
          }}
        >
          <span aria-hidden="true">🟢</span> {t('connected')}
        </div>
      </Switch>

      <ThanxAIConversation
        onMessageSubmit={handleMessageSubmit}
        placeholder={t('reply_placeholder')}
        onCreateAnother={onCreateAnother}
      />
    </div>
  )
}

export default AISegmentBuilderConversation
